import React, { useState, useEffect } from 'react';
import { Store, X, Zap, Trash2, CheckCircle2, Search } from 'lucide-react';
import { createPortal } from 'react-dom';
import { cantinaTabService } from '../../services/cantinaTabService';
import type { Product, CantinaTab, CantinaTabItem } from '../../types';
import { doc, setDoc, updateDoc, increment } from 'firebase/firestore';
import { db, getCollectionPath } from '../../lib/firebase';

interface AdminCantinaTabModalProps {
  isOpen: boolean;
  onClose: () => void;
  tabId: string | null; // null if creating a new one
  clubId: string;
  sessionId: string | null;
  availableProducts: Product[];
  activeUserName: string;
  onSaved: (details?: { tabId?: string, slotNumber?: number, amount: number }) => void;
  operatingDate?: string;
  defaultName?: string;
  slotNumber?: number;
}

export function AdminCantinaTabModal({ 
  isOpen, 
  onClose, 
  tabId, 
  clubId, 
  sessionId, 
  availableProducts, 
  activeUserName,
  onSaved,
  operatingDate,
  defaultName,
  slotNumber
}: AdminCantinaTabModalProps) {
  
  const [tab, setTab] = useState<CantinaTab | null>(null);
  const [tabName, setTabName] = useState('');
  const [cart, setCart] = useState<CantinaTabItem[]>([]);
  
  const [category, setCategory] = useState('all');
  const [search, setSearch] = useState('');
  
  const [showCheckout, setShowCheckout] = useState(false);
  const [paymentCash, setPaymentCash] = useState('');
  const [paymentTransfer, setPaymentTransfer] = useState('');
  
  const [isSaving, setIsSaving] = useState(false);
  const [showConfirmDelete, setShowConfirmDelete] = useState(false);
  const [showFastItemModal, setShowFastItemModal] = useState(false);
  const [fastItemName, setFastItemName] = useState('');
  const [fastItemPrice, setFastItemPrice] = useState('');
  const [fastItemQuantity, setFastItemQuantity] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (tabId) {
        // Load existing tab
        cantinaTabService.getTab(tabId).then(data => {
          if (data) {
            setTab(data);
            setTabName(data.name || '');
            setCart(data.items || []);
          }
        });
      } else {
        // New tab
        setTab(null);
        setTabName('');
        setCart([]);
      }
      setShowCheckout(false);
      setPaymentCash('');
      setPaymentTransfer('');
      setSearchTerm('');
    }
  }, [isOpen, tabId]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (showConfirmDelete) {
          setShowConfirmDelete(false);
        } else if (showFastItemModal) {
          setShowFastItemModal(false);
        } else if (showCheckout) {
          setShowCheckout(false);
        } else {
          onClose();
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, showConfirmDelete, showFastItemModal, showCheckout, onClose]);

  if (!isOpen || typeof document === 'undefined') return null;

  const handleAddToCart = (prod: Product) => {
    const existing = cart.find(i => i.id === prod.id);
    if (existing) {
      setCart(cart.map(i => i.id === prod.id ? { ...i, quantity: i.quantity + 1 } : i));
    } else {
      setCart([...cart, {
        id: prod.id,
        name: prod.name,
        price: prod.price,
        quantity: 1,
        categoryId: prod.category,
        addedAt: Date.now()
      }]);
    }
  };

  const handleRemoveFromCart = (id: string) => {
    const existing = cart.find(i => i.id === id);
    if (!existing) return;
    if (existing.quantity > 1) {
      handleUpdateItemQuantity(id, existing.quantity - 1);
    } else {
      setCart(cart.filter(i => i.id !== id));
    }
  };

  const handleUpdateItemQuantity = (id: string, newQuantity: number) => {
    setCart(cart.map(i => {
      if (i.id === id) {
        const currentPaid = i.paidCount || 0;
        const newPaid = Math.min(currentPaid, newQuantity);
        return { ...i, quantity: Math.max(1, newQuantity), paidCount: newPaid > 0 ? newPaid : undefined };
      }
      return i;
    }));
  };

  const handleUpdateItemNote = (id: string, note: string) => {
    setCart(cart.map(i => i.id === id ? { ...i, note } : i));
  };

  const handleUpdateItemPaidCount = (id: string, paidCount: number) => {
    setCart(cart.map(i => i.id === id ? { ...i, paidCount: paidCount > 0 ? paidCount : undefined } : i));
  };

  const cartTotal = cart.reduce((sum, item) => sum + (item.price * item.quantity), 0);


  const handleDeleteTab = async () => {
    if (!tabId) return;
    setShowConfirmDelete(true);
  };
  
  const confirmDelete = async () => {
    if (!tabId) return;
    setIsSaving(true);
    try {
      // Create a cancelled pos_cantina booking record to show in the register
      const { setDoc, doc } = await import('firebase/firestore');
      const newSaleId = 'pos_cantina_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      const saleDocRef = doc(db, getCollectionPath('bookings'), newSaleId);
      
      const todayStr = operatingDate || new Date().toLocaleDateString('en-CA');
      const timeSlot = new Date().toLocaleTimeString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', hour: '2-digit', minute: '2-digit', hour12: false });
      
      const cancelledSale = {
        id: newSaleId,
        clubId,
        courtName: 'CANTINA',
        playerName: tab?.name || defaultName || 'Mesa',
        date: todayStr,
        timeSlot: timeSlot,
        duration: 0,
        status: 'cancelled',
        cancellationReason: 'Mesa eliminada manualmente',
        isFixed: false,
        price: tab?.totalAmount || cartTotal || 0,
        paymentBreakdown: undefined,
        paymentHistory: [],
        createdAt: Date.now(),
        updatedAt: Date.now()
      };
      
      await setDoc(saleDocRef, JSON.parse(JSON.stringify(cancelledSale)));

      await cantinaTabService.deleteTab(tabId);
      onSaved();
      onClose();
    } catch (e) {
      console.error(e);
      alert('Error al eliminar la mesa');
    } finally {
      setIsSaving(false);
      setShowConfirmDelete(false);
    }
  };

  const handleSaveTab = async () => {
    setIsSaving(true);
    try {
      const activeTabId = tab?.id || tabId;
      if (activeTabId) {

        const { updateDoc } = await import('firebase/firestore');
        const docRef = doc(db, getCollectionPath('cantina_tabs'), activeTabId);
        await updateDoc(docRef, JSON.parse(JSON.stringify({
          name: tabName || defaultName || 'Mesa',
          items: cart,
          totalAmount: cartTotal,
          updatedAt: Date.now()
        })));
      } else {
        // Create new tab
        const finalName = tabName || defaultName || 'Mesa';
        const newId = await cantinaTabService.createTab(clubId, finalName, sessionId, slotNumber);
        const { updateDoc } = await import('firebase/firestore');
        const docRef = doc(db, getCollectionPath('cantina_tabs'), newId);
        await updateDoc(docRef, JSON.parse(JSON.stringify({
          items: cart,
          totalAmount: cartTotal
        })));
      }
      
      onSaved();
      onClose();
    } catch (e) {
      console.error("Error saving tab", e);
      alert("Error al guardar la cuenta");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCloseAndPay = async () => {
    if (cart.length === 0) return;
    
    setIsSaving(true);
    try {
      const activeTabId = tab?.id || tabId;

      // 1. Si la mesa existía previamente, la marcamos como cerrada.
      // Si no existía (venta rápida), no creamos documento de mesa innecesario.
      if (activeTabId) {
        const { updateDoc } = await import('firebase/firestore');
        const docRef = doc(db, getCollectionPath('cantina_tabs'), activeTabId);
        await updateDoc(docRef, JSON.parse(JSON.stringify({
          status: 'closed',
          closedAt: Date.now(),
          updatedAt: Date.now(),
          items: cart,
          totalAmount: cartTotal
        })));
      }

      // 2. Create pos_cantina booking record to enter the cash!
      const newSaleId = 'pos_cantina_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
      const saleDocRef = doc(db, getCollectionPath('bookings'), newSaleId);
      
      const todayStr = operatingDate || new Date().toLocaleDateString('en-CA');
      const timeSlot = new Date().toLocaleTimeString('es-AR', { timeZone: 'America/Argentina/Buenos_Aires', hour: '2-digit', minute: '2-digit', hour12: false });
      
      const cashAmount = Math.min(cartTotal, parseInt(paymentCash || '0'));
      const transferAmount = cartTotal - cashAmount;

      const newSale = {
        id: newSaleId,
        clubId,
        courtName: 'CANTINA',
        playerName: tabName || defaultName || 'Mesa',
        date: todayStr,
        timeSlot: timeSlot,
        duration: 0,
        status: 'paid',
        isFixed: false,
        price: cartTotal,
        paymentBreakdown: { cash: cashAmount, transfer: transferAmount },
        paymentHistory: [{ 
          action: 'initial_charge', 
          timestamp: Date.now(), 
          by: activeUserName || 'Admin', 
          newTotal: cartTotal, 
          newCash: cashAmount, 
          newTransfer: transferAmount 
        }],
        items: cart.map(item => ({ ...item, isPaid: true })),
        createdAt: Date.now(),
        updatedAt: Date.now(),
        hiddenFromGrid: true,
        courtId: 'pos_cantina',
        receiptNumber: ''
      };

      await setDoc(saleDocRef, JSON.parse(JSON.stringify(newSale)));

      // 3. Update sales counts and deduct stock
      const { increment, updateDoc } = await import('firebase/firestore');
      const alerts: string[] = [];

      cart.forEach(item => {
        if (!item.id.startsWith('fast_item_')) {
          const productRef = doc(db, getCollectionPath('products'), item.id);
          const prod = availableProducts.find(p => p.id === item.id);
          
          const updates: any = { salesCount: increment(item.quantity) };

          if (prod && prod.trackStock !== false && prod.stock !== undefined && prod.stock !== null) {
            updates.stock = increment(-item.quantity);
            updates.updatedAt = Date.now();
            
            const predictedStock = Math.max(0, prod.stock - item.quantity);
            if (predictedStock <= (prod.minStockAlert || 0) && prod.stock > (prod.minStockAlert || 0)) {
              alerts.push(`¡Alerta de stock! Quedan aprox. ${predictedStock} unidades de ${prod.name}.`);
            } else if (predictedStock <= (prod.minStockAlert || 0)) {
              alerts.push(`Stock bajo: Quedan aprox. ${predictedStock} unidades de ${prod.name}.`);
            }
          }

          updateDoc(productRef, updates).catch(err => console.log('Error incrementing sales:', err));
        }
      });

      if (alerts.length > 0) {
        setTimeout(() => alert(alerts.join('\n')), 500);
      }

      onSaved({ amount: cartTotal, tabId: activeTabId || undefined, slotNumber: tab?.slotNumber || slotNumber });
      onClose();
    } catch (e) {
      console.error("Error closing tab", e);
      alert("Error al cobrar la cuenta");
    } finally {
      setIsSaving(false);
    }
  };

  const handleCheckoutClick = async () => {
    // Simplemente mostramos el formulario de pago sin guardar nada en la base de datos
    setShowCheckout(true);
    setPaymentCash('');
    setPaymentTransfer(cartTotal.toString());
  };

  const categories = ['all', ...Array.from(new Set(availableProducts.map(p => p.category || 'Otras')))];

  const modalContent = (
    <div className="fixed inset-0 z-[100] bg-black/10 backdrop-blur-[2px] flex items-center justify-center p-2 sm:p-4 overflow-hidden animate-in fade-in duration-200">
      <div className="bg-[#16181f] border border-[#2d313f] rounded-2xl w-full max-w-4xl shadow-[0_20px_60px_rgba(0,0,0,0.8)] overflow-hidden flex flex-col my-auto h-[560px] max-h-[90vh] animate-in zoom-in-95 duration-200">
        
        

                {showFastItemModal && (
          <div className="fixed inset-0 z-[110] bg-black/30 backdrop-blur-[2px] flex items-center justify-center p-4 animate-in fade-in duration-200">
            <div className="bg-[#1a1c23] border border-[#2d313f] rounded-2xl w-full max-w-sm shadow-[0_20px_60px_rgba(0,0,0,0.8)] overflow-hidden animate-in zoom-in-95 duration-200">
              <div className="p-4 border-b border-[#2d313f] flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-amber-500/20 flex items-center justify-center text-[#FFE600]">
                    <Zap size={16} className="fill-[#FFE600]" />
                  </div>
                  <h3 className="font-black text-white uppercase tracking-wider text-sm">Item Rápido</h3>
                </div>
                <button onClick={() => setShowFastItemModal(false)} className="text-zinc-400 hover:text-white p-1 rounded-lg hover:bg-white/5 transition-colors">
                  <X size={18} />
                </button>
              </div>
              
              <div className="p-5 space-y-4">
                <div>
                  <label className="text-[10px] font-black text-zinc-400 uppercase tracking-wider mb-1 block">Nombre del Producto</label>
                  <input
                    type="text"
                    value={fastItemName}
                    onChange={(e) => setFastItemName(e.target.value)}
                    placeholder="Ej: Alquiler de paleta"
                    className="w-full bg-[#15171d] border border-[#2d313f] rounded-xl px-4 py-2.5 text-white text-sm focus:outline-none focus:border-theme-primary/50"
                    autoFocus
                  />
                </div>
                
                <div className="flex gap-3">
                  <div className="flex-1">
                    <label className="text-[10px] font-black text-zinc-400 uppercase tracking-wider mb-1 block">Precio Unitario</label>
                    <div className="relative">
                      <span className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-400 font-bold">$</span>
                      <input
                        type="number"
                        value={fastItemPrice}
                        onChange={(e) => setFastItemPrice(e.target.value)}
                        placeholder="0"
                        min="0"
                        className="w-full bg-[#15171d] border border-[#2d313f] rounded-xl pl-8 pr-4 py-2.5 text-white text-sm focus:outline-none focus:border-theme-primary/50"
                      />
                    </div>
                  </div>
                  <div className="w-32">
                    <label className="text-[10px] font-black text-zinc-400 uppercase tracking-wider mb-1 block">Cantidad</label>
                    <div className="flex items-center bg-[#15171d] rounded-xl border border-[#2d313f] overflow-hidden h-[42px]">
                      <button
                        type="button"
                        onClick={() => setFastItemQuantity(q => Math.max(1, q - 1))}
                        className="w-9 h-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/5 active:scale-90 transition-all font-black text-base cursor-pointer select-none border-r border-[#2d313f]"
                      >
                        -
                      </button>
                      <input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        value={fastItemQuantity}
                        onChange={e => {
                          const val = parseInt(e.target.value);
                          if (!isNaN(val)) setFastItemQuantity(val);
                          else if (e.target.value === '') setFastItemQuantity(1);
                        }}
                        className="flex-1 w-full bg-transparent h-full text-sm text-white font-semibold text-center outline-none"
                      />
                      <button
                        type="button"
                        onClick={() => setFastItemQuantity(q => q + 1)}
                        className="w-9 h-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/5 active:scale-90 transition-all font-black text-base cursor-pointer select-none border-l border-[#2d313f]"
                      >
                        +
                      </button>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="p-4 bg-[#15171d] border-t border-[#2d313f] flex justify-end gap-2">
                <button onClick={() => setShowFastItemModal(false)} className="px-4 py-2 text-zinc-400 font-bold text-xs uppercase tracking-wider hover:text-white transition-colors">
                  Cancelar
                </button>
                <button 
                  onClick={() => {
                    // Si se agregan varios, agregamos iterativamente o modificamos el cart
                    // handleAddToCart agrega de a 1, asi que lo llamamos X veces, o directamente editamos el estado:
                    const newId = 'fast_item_' + Date.now();
                    setCart(prev => [...prev, {
                      id: newId,
                      name: fastItemName.trim() || 'Varios',
                      price: parseInt(fastItemPrice),
                      quantity: fastItemQuantity,
                      categoryId: 'Otras',
                      addedAt: Date.now()
                    }]);
                    setShowFastItemModal(false);
                  }}
                  disabled={!fastItemName.trim() || !fastItemPrice || Number(fastItemPrice) <= 0}
                  className="bg-[#FFE600] hover:bg-[#e6d000] disabled:bg-zinc-800 disabled:text-zinc-600 text-black font-black text-xs uppercase tracking-widest px-5 py-2 rounded-xl transition-all shadow-[0_0_15px_rgba(245,158,11,0.3)] disabled:shadow-none"
                >
                  Agregar
                </button>
              </div>
            </div>
          </div>
        )}

        {showConfirmDelete && (
          <div className="absolute inset-0 z-50 bg-black/20 backdrop-blur-sm flex items-center justify-center p-4 rounded-2xl animate-in fade-in duration-200">
            <div className="bg-[#1a1c23] border border-red-500/30 rounded-xl p-6 max-w-sm w-full flex flex-col items-center text-center shadow-2xl shadow-red-500/10">
              <div className="w-16 h-16 bg-red-500/10 text-red-500 rounded-full flex items-center justify-center mb-4">
                <Trash2 size={32} />
              </div>
              <h3 className="text-lg font-black text-white mb-2">¿Eliminar mesa?</h3>
              <p className="text-sm text-zinc-400 font-bold mb-6">Esta acción no se puede deshacer y la mesa desaparecerá por completo.</p>
              <div className="flex gap-3 w-full">
                <button onClick={() => setShowConfirmDelete(false)} className="flex-1 px-4 py-3 bg-[#2d313f] text-white rounded-xl font-bold text-sm uppercase tracking-wider transition-all hover:bg-[#3d4255]">Cancelar</button>
                <button onClick={confirmDelete} className="flex-1 px-4 py-3 bg-red-500 text-white rounded-xl font-black text-sm uppercase tracking-wider transition-all hover:bg-red-400 shadow-[0_0_15px_rgba(239,68,68,0.3)]">Eliminar</button>
              </div>
            </div>
          </div>
        )}
        
        {/* Header */}
        <div className="py-2.5 px-4 bg-[#222530] border-b border-[#2d313f] flex items-center justify-between relative sticky top-0 z-10 min-h-[50px] shrink-0">
          
          {/* Título central */}
          <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none">
            <h3 className="text-sm font-black uppercase tracking-widest text-white">
              Venta de Cantina
            </h3>
          </div>

          <div className="flex items-center gap-3 relative z-10">
            <Store size={20} className="text-theme-primary" />
            <div className={`flex items-center gap-2 text-[11px] font-bold text-zinc-400 bg-[#16181f] px-2.5 py-1 rounded-lg border transition-all duration-500 ${!tabId && !tabName ? 'border-theme-primary ring-2 ring-theme-primary/30 shadow-[0_0_15px_rgba(0,0,0,0.5)] animate-[pulse_1.5s_ease-in-out_infinite]' : 'border-[#2d313f]'}`}>
              <span>Cuenta:</span>
              <input
                type="text"
                value={tabName}
                onChange={(e) => setTabName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.currentTarget.blur();
                  }
                }}
                style={{ width: `${Math.max(tabName.length || (defaultName?.length || 4), 10)}ch` }}
                className="bg-transparent text-white font-black focus:outline-none placeholder-zinc-600 text-center transition-all duration-200"
                placeholder={defaultName || "Nombre"}
                autoFocus={!tabId}
              />
            </div>
          </div>
          
          <div className="flex items-center gap-2 relative z-10">
            {tabId && (
              <button
                onClick={handleDeleteTab}
                className="p-1.5 bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 rounded-lg transition-all mr-2"
                title="Eliminar Mesa"
              >
                <Trash2 size={16} />
              </button>
            )}
            <button
              onClick={onClose}
              className="p-1.5 bg-white/5 hover:bg-white/10 text-zinc-400 hover:text-white rounded-lg transition-all"
            >
              <X size={16} />
            </button>
          </div>

        </div>

        <div className="flex flex-col md:flex-row flex-1 overflow-hidden">
          {/* Left panel: Ticket */}
          <div className="w-full md:w-[420px] bg-[#1a1c23] border-r border-[#2d313f] flex flex-col shrink-0">
            <div className="p-4 border-b border-[#2d313f] flex justify-between items-center">
              <span className="text-[10px] font-black uppercase tracking-wider text-zinc-400">Detalle del Ticket</span>
              <span className="text-[10px] font-black uppercase tracking-wider text-theme-primary">{cart.length} items</span>
            </div>
            
            <div className="flex-1 overflow-y-auto p-4 space-y-2 custom-scrollbar">
              {cart.length === 0 ? (
                <div className="text-center py-6 border border-dashed border-[#2d313f] rounded-lg text-zinc-600 text-xs font-bold uppercase tracking-wider">
                  Mesa vacía
                </div>
              ) : (
                cart.map(item => (
                  <div key={item.id} className="flex items-center gap-1 sm:gap-2 bg-[#222530] p-1.5 sm:p-2 rounded-xl border border-[#2d313f] hover:border-zinc-700 hover:bg-[#282b3a] transition-all duration-200 animate-in fade-in duration-150">
                    <div className="flex-1 flex items-center min-w-0">
                      <div className="min-w-0 flex-1 flex flex-col justify-center">
                        <span className="text-white/90 font-semibold text-[9px] sm:text-[10px] leading-tight line-clamp-1">{item.name}</span>
                        <input
                          type="text"
                          defaultValue={item.note || ''}
                          placeholder="Añadir nota..."
                          onBlur={(e) => {
                            const val = e.target.value.trim();
                            if (val !== (item.note || '').trim()) {
                              handleUpdateItemNote(item.id, val);
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') e.currentTarget.blur();
                          }}
                          className="bg-transparent border-none outline-none text-zinc-400 text-[9px] sm:text-[10px] block w-full mt-0.5 placeholder:text-zinc-600 focus:text-theme-primary transition-colors"
                        />
                      </div>
                    </div>

                    {/* Controles de cantidad */}
                    <div className="shrink-0 w-[60px] sm:w-[75px] flex items-center justify-center">
                      <div className="flex items-center bg-[#15171d]/90 border border-[#2d313f] rounded-lg overflow-hidden h-5 sm:h-6">
                        <button
                          type="button"
                          onClick={() => handleUpdateItemQuantity(item.id, item.quantity - 1)}
                          disabled={item.quantity <= 1}
                          className="w-4 sm:w-5 h-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/5 active:scale-90 disabled:opacity-30 disabled:cursor-not-allowed transition-all text-[11px] sm:text-xs font-black cursor-pointer select-none border-r border-[#2d313f]"
                        >
                          -
                        </button>
                        <span key={`qty-${item.quantity}`} className="text-theme-primary font-bold text-[9px] sm:text-[10px] px-1 sm:px-1.5 text-center min-w-[16px] sm:min-w-[20px] select-none">
                          {item.quantity}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleUpdateItemQuantity(item.id, item.quantity + 1)}
                          className="w-4 sm:w-5 h-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/5 active:scale-90 transition-all text-[11px] sm:text-xs font-black cursor-pointer select-none border-l border-[#2d313f]"
                        >
                          +
                        </button>
                      </div>
                    </div>

                    {/* Pago Parcial */}
                    <div className="shrink-0 w-[100px] sm:w-[120px] flex items-center justify-center min-w-0">
                      { (item.paidCount || 0) > 0 ? (
                        <div className="flex items-center gap-1 sm:gap-1.5 bg-[#2d313f] p-1 rounded-lg border border-zinc-700/50 max-w-full">
                          <button
                            type="button"
                            onClick={() => handleUpdateItemPaidCount(item.id, 0)}
                            className="w-6 sm:w-7 h-3.5 sm:h-4 bg-green-500 rounded-full p-0.5 flex items-center transition-all cursor-pointer shrink-0 shadow-[0_0_8px_rgba(34,197,94,0.4)]"
                            title="Desactivar cobro parcial"
                          >
                            <div className="w-2.5 sm:w-3 h-2.5 sm:h-3 bg-black rounded-full shadow-md transform translate-x-2.5 sm:translate-x-3 transition-transform" />
                          </button>

                          <div className="flex items-center bg-[#15171d]/90 border border-[#2d313f] rounded-md overflow-hidden h-5 sm:h-6">
                            <button
                              type="button"
                              onClick={() => handleUpdateItemPaidCount(item.id, (item.paidCount || 1) - 1)}
                              disabled={(item.paidCount || 1) <= 1}
                              className="w-5 sm:w-6 h-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/5 active:scale-90 disabled:opacity-30 disabled:cursor-not-allowed transition-all text-[11px] sm:text-xs font-black cursor-pointer select-none border-r border-[#2d313f]"
                            >
                              -
                            </button>
                            <span key={`paid-${item.paidCount}-${item.quantity}`} className="text-green-400 font-bold text-[9px] sm:text-[10px] px-2 text-center min-w-[28px] sm:min-w-[32px] select-none">
                              {item.paidCount || 1}/{item.quantity}
                            </span>
                            <button
                              type="button"
                              onClick={() => handleUpdateItemPaidCount(item.id, (item.paidCount || 1) + 1)}
                              disabled={(item.paidCount || 1) >= item.quantity}
                              className="w-5 sm:w-6 h-full flex items-center justify-center text-zinc-400 hover:text-white hover:bg-white/5 active:scale-90 disabled:opacity-30 disabled:cursor-not-allowed transition-all text-[11px] sm:text-xs font-black cursor-pointer select-none border-l border-[#2d313f]"
                            >
                              +
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center">
                          <button
                            type="button"
                            onClick={() => handleUpdateItemPaidCount(item.id, 1)}
                            className="w-8 h-4 bg-[#15171d] border border-zinc-700/50 rounded-full p-0.5 flex items-center transition-all cursor-pointer hover:border-zinc-500"
                            title="Activar cobro parcial"
                          >
                            <div className="w-3 h-3 bg-zinc-500 rounded-full shadow-md transform translate-x-0 transition-transform" />
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Total y basura */}
                    <div className="shrink-0 w-[55px] sm:w-[70px] md:w-[85px] flex items-center justify-end gap-0.5 min-w-0 pr-0.5">
                      <span className="text-zinc-200 font-bold text-[8px] sm:text-[9px] text-right truncate">${(item.price * item.quantity).toLocaleString('es-AR')}</span>
                      <button
                        onClick={() => setCart(cart.filter(i => i.id !== item.id))}
                        className="text-red-400 hover:text-white p-1 bg-red-500/10 hover:bg-red-500/30 rounded-lg transition-all duration-200 hover:scale-110 active:scale-90 cursor-pointer shrink-0 ml-0.5"
                        title="Quitar consumo"
                      >
                        <Trash2 size={11} className="sm:w-3 sm:h-3" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="p-4 bg-[#16181f] border-t border-[#2d313f] flex flex-col gap-3">
              <div className="flex justify-start items-center gap-3 text-lg font-black text-white">
                <span>TOTAL:</span>
                <span>${cartTotal.toLocaleString('es-AR')}</span>
              </div>
              
              {!showCheckout ? (
                <div className="flex gap-2">
                  <button 
                    onClick={handleSaveTab}
                    disabled={isSaving}
                    className="flex-1 py-3 rounded-xl font-black text-xs uppercase tracking-wider transition-all bg-blue-500 hover:bg-blue-400 text-white shadow-[0_0_15px_rgba(59,130,246,0.3)] [text-shadow:0_1px_2px_rgba(0,0,0,0.5)]"
                  >
                    Guardar venta
                  </button>
                  {cartTotal > 0 && (
                    <button 
                      onClick={handleCheckoutClick}
                      className="flex-1 py-3 rounded-xl font-black text-xs uppercase tracking-wider transition-all bg-[#72b512] hover:bg-[#84cc16] text-white shadow-md [text-shadow:0_1px_2px_rgba(0,0,0,0.5)]"
                    >
                      Cobrar
                    </button>
                  )}
                </div>
              ) : (
                <div className="flex flex-col gap-3 animate-in slide-in-from-bottom-4">
                  <div className="grid grid-cols-2 gap-2">
                    <div className="flex flex-col">
                      <span className="text-[9px] font-black uppercase tracking-widest text-zinc-500 mb-1">Efectivo</span>
                      <input
                        type="tel"
                        value={paymentCash ? parseInt(paymentCash).toLocaleString('es-AR') : ''}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '');
                          let numVal = parseInt(val || '0');
                          if (numVal > cartTotal) numVal = cartTotal;
                          setPaymentCash(numVal === 0 ? '' : numVal.toString());
                          setPaymentTransfer((cartTotal - numVal).toString());
                        }}
                        className="bg-[#1a1c23] border border-[#2d313f] rounded-lg p-2 text-white font-bold text-center"
                        placeholder="0"
                      />
                    </div>
                    <div className="flex flex-col">
                      <span className="text-[9px] font-black uppercase tracking-widest text-zinc-500 mb-1">Transferencia</span>
                      <input
                        type="tel"
                        value={paymentTransfer ? parseInt(paymentTransfer).toLocaleString('es-AR') : ''}
                        onChange={(e) => {
                          const val = e.target.value.replace(/\D/g, '');
                          let numVal = parseInt(val || '0');
                          if (numVal > cartTotal) numVal = cartTotal;
                          setPaymentTransfer(numVal === 0 ? '' : numVal.toString());
                          setPaymentCash((cartTotal - numVal).toString());
                        }}
                        className="bg-[#1a1c23] border border-[#2d313f] rounded-lg p-2 text-white font-bold text-center"
                        placeholder="0"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => setShowCheckout(false)} className="px-3 py-2 bg-[#2d313f] text-white rounded-lg font-bold text-xs uppercase">Atrás</button>
                    <button onClick={handleCloseAndPay} disabled={isSaving} className="flex-1 bg-[#72b512] hover:bg-[#84cc16] text-white rounded-lg font-black text-sm uppercase tracking-wider shadow-md flex items-center justify-center gap-2 [text-shadow:0_1px_2px_rgba(0,0,0,0.5)]">
                      <CheckCircle2 size={16} /> Confirmar Pago
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Right panel: Products */}
          <div className="flex-1 flex flex-col bg-[#16181f]">
            <div className="p-3 sm:p-4 flex flex-col sm:flex-row gap-3 border-b border-[#2d313f] items-center">
              <div className="flex gap-2 w-full sm:w-auto shrink-0">
                <button
                  onClick={() => {
                    setFastItemName('');
                    setFastItemPrice('');
                    setFastItemQuantity(1);
                    setShowFastItemModal(true);
                  }}
                  className="flex items-center gap-2 px-4 py-2 bg-transparent hover:bg-blue-400/10 border border-blue-400 rounded-lg transition-all active:scale-95"
                >
                  <Zap size={16} className="text-blue-400 fill-blue-400" />
                  <span className="text-[10px] font-black text-white uppercase tracking-wider">Item Rápido</span>
                </button>
              </div>
              <div className="flex-1 w-full max-w-sm relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" size={14} />
                <input 
                  type="text" 
                  placeholder="Buscar producto..." 
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="w-full bg-[#1a1c23] border border-[#2d313f] rounded-lg pl-9 pr-3 py-2 text-xs text-white focus:border-theme-primary/50 focus:outline-none placeholder:text-zinc-500"
                />
              </div>
            </div>

            <div className="flex-1 p-4 overflow-y-auto custom-scrollbar">
              
              <div className="flex flex-wrap gap-2 justify-center pb-2">
                
                {[...availableProducts]
                  .sort((a, b) => ((b as any).salesCount || 0) - ((a as any).salesCount || 0))
                  .filter(p => {
                    const matchesCategory = category === 'all' || p.category === category;
                    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase());
                    return matchesCategory && matchesSearch;
                  })
                  .map(prod => {
                  const qty = cart.find(i => i.id === prod.id)?.quantity || 0;
                  return (
                    <div
                      key={prod.id}
                      onClick={() => handleAddToCart(prod)}
                      className="relative flex flex-col items-center justify-between p-1 rounded-xl bg-[#1e202b] border border-[#2d313f] hover:bg-[#252836] hover:border-theme-primary/50 cursor-pointer transition-all active:scale-95 group w-[85px] sm:w-[95px] h-[85px] sm:h-[95px] shrink-0 select-none"
                    >
                      {qty > 0 && (
                        <div
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRemoveFromCart(prod.id);
                          }}
                          className="absolute -top-1.5 -right-1.5 bg-blue-500 text-white font-black text-[10px] w-5 h-5 flex items-center justify-center rounded-full shadow-lg border border-blue-400 z-10 hover:bg-red-500 hover:border-red-600 transition-colors group/badge cursor-pointer select-none"
                        >
                          <span className="group-hover/badge:hidden">{qty}</span>
                          <span className="hidden group-hover/badge:block text-xs leading-none mt-[1px]">-</span>
                        </div>
                      )}
                      <div className="w-full flex justify-center flex-1 items-center min-h-[42px] mt-1">
                        {prod.imageUrl ? (
                          <img
                            src={prod.imageUrl}
                            alt={prod.name}
                            className="w-10 h-10 sm:w-11 sm:h-11 rounded-lg object-cover group-hover:scale-110 transition-transform shrink-0"
                          />
                        ) : (
                          <Store size={22} className="text-zinc-500 group-hover:scale-110 transition-transform sm:w-6 sm:h-6 shrink-0" />
                        )}
                      </div>
                      <div className="flex flex-col items-center mt-auto pb-0.5 w-full">
                        <span className="text-[9px] font-bold text-white text-center line-clamp-1 leading-tight w-full px-1">{prod.name}</span>
                        <span className="text-[9px] text-zinc-400 font-bold leading-none mt-0.5">${prod.price.toLocaleString('es-AR')}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  return createPortal(modalContent, document.getElementById('root') || document.body);
}
